import { makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { isPowerOperator } from "@/models/utils/semanticChecks";

import { OperatorSymbolNodeModel } from "./OperatorSymbolNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type {
  VisitableNode,
  NodeVisitor,
  InlineEditableNode,
} from "@/models/nodes/visitors/NodeVisitor";
import type { TailwindColor } from "@/utils/tailwindColors";
import type {
  FunctionNodeProps,
  FunctionNodeConstructorProps,
  FunctionView,
  FunctionPowerPlacement,
  ClosingSymbol,
  OpeningSymbol,
} from "@corca/data";

export class FunctionNodeModel
  extends BaseNodeModel<FunctionNodeProps>
  implements VisitableNode, InlineEditableNode
{
  static type = "function" as const;

  readonly inlineEditable = true as const;

  isMergeable = false;

  isSplittable = false;

  static validateValue(this: void, _value: string, _allowIncomplete?: boolean) {
    // We only allow function suggestions so we validate the value in the input.
    return false;
  }

  validateValue = FunctionNodeModel.validateValue;

  type = FunctionNodeModel.type;

  static getFunctionByValue(doc: DocumentModel, value: string) {
    const functions = doc.getNodesByType(FunctionNodeModel.type);
    return functions.find((node) => node.value === value);
  }

  protected fillRequiredProps(props: FunctionNodeConstructorProps): FunctionNodeProps {
    return props;
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<FunctionNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      inlineNode: true,
      symbolNode: true,
      parameters: true,
      power: true,
      variables: true,
      value: true,
      omitParentheses: true,
      paramsAreEmpty: true,
    });
  }

  static create(
    doc: DocumentModel,
    value: string | SymbolNodeModel,
    props: FunctionNodeConstructorProps = {},
    nodes: NodeModel[] = [],
    id?: NodeId,
  ) {
    const symbolNode = typeof value === "string" ? doc.create(SymbolNodeModel, value) : value;
    const color = props.color || FunctionNodeModel.getFunctionByValue(doc, symbolNode.value)?.color;

    if (nodes.length === 0) {
      nodes.push(doc.create(InlineNodeModel));
    }

    return new FunctionNodeModel(doc, id, { ...props, color }, [symbolNode, ...nodes]);
  }

  get isEditable(): boolean {
    return true;
  }

  get symbolNode() {
    return this.children.find((n) => n.is(SymbolNodeModel));
  }

  get parameters() {
    return this.children.filter((n) => n.is(InlineNodeModel));
  }

  get inlineNode() {
    return this.children.find((n) => n.is(InlineNodeModel));
  }

  get power() {
    return this.children.find(isPowerOperator);
  }

  get variables(): VariableNodeModel[] {
    return this.parameters.reduce<VariableNodeModel[]>((acc, node) => {
      if (node.nodes.length === 1 && node.nodes[0].is(VariableNodeModel)) {
        return [...acc, node.nodes[0]];
      }

      return acc;
    }, []);
  }

  get value(): string {
    const childSymbolNode = this.descendants.find((n) => n.is(SymbolNodeModel));
    return childSymbolNode?.props.value || "";
  }

  set value(value: string) {
    const childSymbolNode = this.descendants.find((n) => n.is(SymbolNodeModel));
    if (childSymbolNode) {
      childSymbolNode.value = value;
    }
  }

  get name(): string {
    return this.props.name || "";
  }

  set name(value: string) {
    this.updateProps({ name: value });
  }

  get color(): TailwindColor | undefined {
    return this.props.color;
  }

  set color(value: TailwindColor) {
    this.updateProps({ color: value });
  }

  /** @deprecated */
  get functionView(): FunctionView {
    return this.props.functionView || "default";
  }

  /** @deprecated */
  set functionView(functionView: FunctionView) {
    this.updateProps({ functionView });
  }

  get isBuiltIn(): boolean {
    return !!this.props.isBuiltIn;
  }

  get parenthesesVisibility() {
    return this.props.parenthesesVisibility || "visible";
  }

  set parenthesesVisibility(value) {
    this.updateProps({ parenthesesVisibility: value });
  }

  get omitParentheses(): boolean {
    switch (this.parenthesesVisibility) {
      case "hidden":
        return true;

      case "visible":
        return false;

      case "auto":
        return this.shouldOmitParentheses;
    }
  }

  get shouldOmitParentheses(): boolean {
    if (this.parameters.length > 1) {
      return false;
    }

    const [inlineNode] = this.parameters ?? [];

    if (inlineNode?.nodes.length === 0) {
      return true;
    }

    if (inlineNode?.nodes.length === 1) {
      const [node] = inlineNode.nodes;

      switch (node?.type) {
        case "group": {
          return !!(node.openingSymbol && node.closingSymbol);
        }

        default: {
          return true;
        }
      }
    }

    return !inlineNode?.nodes.some((n) => n.is(OperatorSymbolNodeModel) || n.is(GroupNodeModel));
  }

  get placement(): NonNullable<FunctionNodeProps["placement"]> {
    return this.props.placement ?? "prefix";
  }

  get powerPlacement(): FunctionPowerPlacement {
    return this.props.powerPlacement ?? "child";
  }

  get openingSymbol() {
    return this.props.openingSymbol ?? "(";
  }

  set openingSymbol(openingSymbol: OpeningSymbol | undefined) {
    this.updateProps({ openingSymbol });
  }

  get closingSymbol() {
    return this.props.closingSymbol ?? ")";
  }

  set closingSymbol(closingSymbol: ClosingSymbol | undefined) {
    this.updateProps({ closingSymbol });
  }

  get paramsAreEmpty(): boolean {
    return this.parameters.every((param) => param.isEmpty);
  }

  get hasParams(): boolean {
    return this.parameters.length > 0;
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitFunctionNode(this);
  }
}
